import { CSSProperties } from 'react';

export const PREFIX = 'rwl';

export function varPrefix() {
    return `--${PREFIX}`;
}

export const theme: Theme = {
    color: {
        primary: 'blue',
        warning: 'rose',
        success: 'emerald',
        neutral: 'trueGray',
    },
    background: {
        light: {
            primary: 'var(--rwl-palette-gray-50)',
            level1: 'var(--rwl-palette-gray-100)',
            level2: 'var(--rwl-palette-gray-200)',
        },
        dark: {
            primary: 'var(--rwl-palette-gray-900)',
            level1: 'var(--rwl-palette-gray-800)',
            level2: 'var(--rwl-palette-gray-700)',
        },
    },
    text: {
        light: {
            default: '#1E1E1E',
            alternative: '#FFFFFFE0',
            hint: 'var(--rwl-palette-gray-500)',
        },
        dark: {
            default: '#FFFFFFE0',
            alternative: '#1E1E1E',
            hint: '#FFFFFF50',
        },
    },
    icon: {
        default: '#000064',
    },
    spacing: {
        xs: '0.25rem',
        sm: '.5rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '3rem',
    },
    shadow: {
        xs: '0px 1px 2px rgba(0, 0, 0, 0.05)',
        sm: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        md: '0px 3px 3px -2px rgba(0, 0, 0, 0.2), 0px 3px 4px 0px rgba(0, 0, 0, 0.14), 0px 1px 8px 0px rgba(0, 0, 0, 0.12)',
        lg: '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 1px 18px 0px rgba(0, 0, 0, 0.12)',
        xl: '0px 4px 6px -1px rgba(0, 0, 0, 0.2), 0px 2px 24px 0px rgba(0, 0, 0, 0.12)',
    },
    radius: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
    },
    breakpoints: {
        xs: 480,
        sm: 768,
        md: 1000,
        lg: 1194, // iPad Pro 11" landscape
        xl: 1512, // MacBook Pro 14"
    },
    typography: {
        h1: {
            fontSize: '2.25rem',
            fontWeight: 700,
            lineHeight: '150%',
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: '150%',
        },
        h3: {
            fontSize: '1.75rem',
            fontWeight: 700,
            lineHeight: '150%',
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 700,
            lineHeight: '150%',
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: 700,
            lineHeight: '150%',
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: '160%',
        },
        subtitle1: {
            fontSize: '1rem',
            fontWeight: 700,
            lineHeight: '175%',
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 700,
            lineHeight: '157%',
        },
        body1: {
            fontSize: '.875rem',
            fontWeight: 400,
            lineHeight: '150%',
        },
        body2: {
            fontSize: '0.75rem',
            fontWeight: 400,
            lineHeight: '143%',
        },
        chip: {
            fontSize: '0.75rem',
            fontWeight: 600, // yes, we know this is 600 not 700
            lineHeight: '143%',
        },
        caption1: {
            fontSize: '0.81rem',
            fontWeight: 400,
            lineHeight: '143%',
        },
        caption2: {
            fontSize: '0.68rem',
            fontWeight: 400,
            lineHeight: '143%',
        },
        overline: {
            textTransform: 'uppercase',
            fontSize: '0.68rem',
            fontWeight: 700,
            lineHeight: '266%',
        },
        fontFamily: '"Inter", "Arial", "sans-serif"',
        fontImports: [
            // Inter, regular (400) and semi-bold (600).  Not currently importing italics or other weights in order to save bandwidth.
            'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700&display=swap',
        ],
    },

    jobCardMedia: {
        height: {
            xs: '148px',
            sm: '148px',
            md: '186px',
            lg: '186px',
        },
        width: {
            xs: '148px',
            sm: '148px',
            md: '148px',
            lg: '246px',
        },
    },
};

export type ThemeBreakpoint = keyof Theme['breakpoints'];

export const mqDown = (breakpoint: ThemeBreakpoint) =>
    `@container (max-width: ${(theme.breakpoints[breakpoint] || 1) - 1}px)`;

export const mqUp = (breakpoint: ThemeBreakpoint) =>
    `@container (min-width: ${theme.breakpoints[breakpoint]}px)`;

export interface ThemeColorVariants {
    default: string;
    hover: string;
    active: string;
    map: string;
    'map-active': string;
    light: string;
    'light-hover': string;
    'light-active': string;
    border: string;
}

export type ThemeColor = string | ThemeColorVariants;

export interface ThemeColors {
    primary: ThemeColor;
    warning: ThemeColor;
    success: ThemeColor;
    neutral: ThemeColor;
}

interface ThemeBackgrounds {
    primary: ThemeColor;
    level1: ThemeColor;
    level2: ThemeColor;
}

interface ThemeText {
    default: ThemeColor;
    alternative: ThemeColor;
    hint: ThemeColor;
}

interface ThemeSize<T> {
    xs: T;
    sm: T;
    md: T;
    lg: T;
    xl?: T;
}

export interface ThemeTypography {
    fontFamily: string;
    fontImports?: string[];

    h1: CSSProperties;
    h2: CSSProperties;
    h3: CSSProperties;
    h4: CSSProperties;
    h5: CSSProperties;

    h6: CSSProperties;

    subtitle1: CSSProperties;
    subtitle2: CSSProperties;

    body1: CSSProperties;
    body2: CSSProperties;

    chip: CSSProperties;

    caption1: CSSProperties;
    caption2: CSSProperties;

    overline: CSSProperties;
}

export type TypographyStyles = Omit<
    ThemeTypography,
    'fontFamily' | 'fontImports'
>;

export interface JobCardMediaDimensions {
    height: ThemeSize<string>;
    width: ThemeSize<string>;
}

export interface Theme {
    color: ThemeColors;
    background: { light: ThemeBackgrounds; dark: ThemeBackgrounds };
    text: { light: ThemeText; dark: ThemeText };
    icon: { default: ThemeColor };
    spacing: ThemeSize<string>;
    shadow: ThemeSize<string>;
    radius: ThemeSize<string>;
    breakpoints: ThemeSize<number>;

    typography: ThemeTypography;
    // fontSize: ThemeSize;
    jobCardMedia: JobCardMediaDimensions;
}
